import React from "react";
import {
  LayoutDashboard,
  Users,
  Banknote,
  Landmark,
  PieChart,
  ShieldAlert,
  Settings,
  CircleDollarSign,
  BookOpen,
  CreditCard,
  LogOut,
  MessageSquare
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarSeparator,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { BranchSelector } from "@/components/BranchSelector";
import { useRoleStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
export function AppSidebar(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useRoleStore((s) => s.role);
  const logout = useRoleStore((s) => s.logout);
  const staffNavItems = [
    { title: "Dashboard", icon: LayoutDashboard, path: "/", roles: ['Admin', 'Manager', 'Officer', 'Accountant'] },
    { title: "Clients", icon: Users, path: "/clients", roles: ['Admin', 'Manager', 'Officer', 'Accountant'] },
    { title: "Daily Collections", icon: Banknote, path: "/collections", roles: ['Admin', 'Manager', 'Officer'] },
    { title: "Loan Management", icon: Landmark, path: "/loans", roles: ['Admin', 'Manager', 'Accountant'] },
    { title: "Messages", icon: MessageSquare, path: "/messages", roles: ['Admin', 'Manager', 'Officer', 'Accountant'] },
  ];
  const adminItems = [
    { title: "Financial Reports", icon: PieChart, path: "/reports", roles: ['Admin', 'Manager', 'Accountant'] },
    { title: "Audit Logs", icon: ShieldAlert, path: "/audit-logs", roles: ['Admin', 'Manager'] },
    { title: "Settings", icon: Settings, path: "/settings", roles: ['Admin', 'Manager', 'Officer', 'Accountant'] },
  ];
  const clientNavItems = [
    { title: "Client Zone", icon: LayoutDashboard, path: "/client/dashboard", roles: ['Client'] },
    { title: "My Passbook", icon: BookOpen, path: "/client/passbook", roles: ['Client'] },
    { title: "Make Payment", icon: CreditCard, path: "/client/payment", roles: ['Client'] },
    { title: "Settings", icon: Settings, path: "/settings", roles: ['Client'] },
  ];
  const filteredNav = role === 'Client' ? clientNavItems : staffNavItems.filter(item => item.roles.includes(role));
  const filteredAdmin = role === 'Client' ? [] : adminItems.filter(item => item.roles.includes(role));
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const showBranchSelector = role === 'Admin' || role === 'Accountant' || role === 'Manager';
  return (
    <Sidebar className="sidebar-gradient border-r border-sidebar-border scrollbar-custom">
      <SidebarHeader className="h-20 flex items-center px-6">
        <Link to={role === 'Client' ? "/client/dashboard" : "/"} className="flex items-center gap-3 group">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground shadow-button group-hover:scale-105 transition-transform">
            <CircleDollarSign className="h-6 w-6" />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-extrabold leading-tight tracking-tight text-sidebar-foreground">
              {role === 'Client' ? 'Client Zone' : 'Perfect Match'}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-sidebar-foreground/70">Susu PLC</span>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarSeparator className="opacity-60" />
      <SidebarContent className="px-3 py-6 scrollbar-custom">
        <SidebarGroup>
          <SidebarGroupLabel className="mb-4 px-4 text-[10px] font-extrabold uppercase tracking-widest text-sidebar-foreground/70">
            {role === 'Client' ? 'Your Portfolio' : 'Banking Operations'}
          </SidebarGroupLabel>
          <SidebarMenu className="gap-1.5">
            {filteredNav.map((item) => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname === item.path}
                  className={cn(
                    "h-11 px-4 rounded-xl transition-all duration-200",
                    location.pathname === item.path
                      ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-button hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground"
                      : "text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                >
                  <Link to={item.path}>
                    <item.icon className={cn("size-5 shrink-0", location.pathname === item.path ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/75")} />
                    <span className="font-bold">{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        {role !== 'Client' && filteredAdmin.length > 0 && (
          <SidebarGroup className="mt-8">
            <SidebarGroupLabel className="mb-4 px-4 text-[10px] font-extrabold uppercase tracking-widest text-sidebar-foreground/70">
              Institutional Control
            </SidebarGroupLabel>
            <SidebarMenu className="gap-1.5">
              {filteredAdmin.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    className={cn(
                      "h-11 px-4 rounded-xl transition-all duration-200",
                      location.pathname === item.path
                        ? "bg-sidebar-foreground text-sidebar-background shadow-button hover:bg-sidebar-foreground/90 hover:text-sidebar-background"
                        : "text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    )}
                  >
                    <Link to={item.path}>
                      <item.icon className={cn("size-5 shrink-0", location.pathname === item.path ? "text-sidebar-background" : "text-sidebar-foreground/75")} />
                      <span className="font-bold">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter className="flex flex-col gap-3 border-t border-sidebar-border bg-sidebar/80 p-4 backdrop-blur-sm">
        {role !== 'Client' ? (
          <div className="space-y-3">
            <RoleSwitcher />
            <div className="transition-all duration-300">
              {showBranchSelector && <BranchSelector />}
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            className="h-11 w-full justify-start rounded-xl text-sidebar-foreground/80 hover:bg-destructive/10 hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Exit Portal
          </Button>
        )}
        <div className="flex flex-col gap-1 mt-2 px-2">
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-sidebar-foreground/70">Enterprise v2.0</p>
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <p className="text-[9px] font-bold uppercase tracking-tight text-sidebar-foreground/70">Bank-Grade Secure</p>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
